import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Wallet, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageTitle } from '../hooks/usePageTitle';
import { useT } from '../contexts/LanguageContext';
import { loadRazorpay, inr } from '../lib/loadRazorpay';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Row = ({ label, value, testId, strong }) => (
  <div className="flex justify-between py-2 border-b border-charcoal-100 last:border-0 text-sm">
    <span className="text-charcoal-500">{label}</span>
    <span className={strong ? 'font-semibold text-saffron-700' : 'text-charcoal-800'} data-testid={testId}>{value}</span>
  </div>
);

export default function PayBalance() {
  usePageTitle('Pay Balance');
  const tr = useT();
  const { registrationId } = useParams();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') || '');
  const [info, setInfo] = useState(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const lookup = async (mail) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/registrations/${registrationId}/balance`, { params: { email: mail } });
      setInfo(data);
      setAmount(String(data.balance / 100));
    } catch {
      setInfo(null);
      toast.error(tr('We could not find this registration for that email.'));
    }
  };

  useEffect(() => { if (params.get('email')) lookup(params.get('email')); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async (e) => {
    e.preventDefault();
    const paise = Math.round(parseFloat(amount || '0') * 100);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/registrations/pay-balance`, { registration_id: registrationId, email, amount: paise });
      if (!(await loadRazorpay())) throw new Error('gateway');
      new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: data.amount,
        currency: data.currency,
        name: 'Rishidev Foundation',
        description: `Balance · ${info.title}`,
        order_id: data.order_id,
        prefill: { name: info.participant_name, email },
        theme: { color: '#E67E22' },
        handler: async (resp) => {
          try {
            const { data: v } = await axios.post(`${API_URL}/api/registrations/verify`, resp);
            setInfo({ ...info, paid: v.paid, balance: v.balance, status: v.registration_status, justPaid: v.this_payment });
            setAmount(String(v.balance / 100));
          } catch {
            toast.error(tr('Payment verification failed. Please contact us.'));
          } finally { setBusy(false); }
        },
        modal: { ondismiss: () => setBusy(false) },
      }).open();
    } catch (err) {
      toast.error(err.response?.data?.detail || tr('Could not start payment'));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-cream" data-testid="pay-balance-page">
      <section className="py-20 px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg mx-auto bg-white rounded-2xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 p-8">
          <div className="w-14 h-14 rounded-full bg-saffron-100 text-saffron-600 flex items-center justify-center mb-4"><Wallet size={26} /></div>
          <h1 className="text-3xl font-heading text-charcoal-800 mb-2">{tr('Pay Remaining Balance')}</h1>
          {!info ? (
            <form onSubmit={(e) => { e.preventDefault(); lookup(email); }} className="space-y-4 mt-6">
              <p className="text-sm text-charcoal-600">{tr('Enter the email you registered with.')}</p>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="pay-balance-email" className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
              <button type="submit" data-testid="pay-balance-lookup" className="w-full bg-saffron-600 text-white rounded-full px-6 py-3 font-medium hover:bg-saffron-500 transition-colors">{tr('Continue')}</button>
            </form>
          ) : (
            <div className="mt-4">
              <p className="text-charcoal-600 mb-4" data-testid="pay-balance-title">{info.title}</p>
              <div className="rounded-xl bg-saffron-50/40 border border-saffron-100 px-4 py-2 mb-6">
                <Row label={tr('Total Fee')} value={inr(info.total)} testId="pay-balance-total" />
                <Row label={tr('Paid So Far')} value={inr(info.paid)} testId="pay-balance-paid" />
                <Row label={tr('Balance Due')} value={inr(info.balance)} testId="pay-balance-due" strong />
              </div>
              {info.justPaid > 0 && (
                <div className="flex items-start gap-2 text-sm text-green-700 bg-green-50 rounded-lg p-3 mb-4" data-testid="pay-balance-success">
                  <CheckCircle2 size={18} className="mt-0.5" /> {tr('Payment received')} ({inr(info.justPaid)}). {tr('A receipt has been emailed to you.')}
                </div>
              )}
              {info.balance > 0 ? (
                <form onSubmit={pay} className="space-y-3">
                  <label className="block text-sm font-medium text-charcoal-700">{tr('Amount to pay now (₹)')}</label>
                  <input type="number" required min={info.min_installment / 100} max={info.balance / 100} step="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pay-balance-amount" className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500" />
                  <p className="text-xs text-charcoal-500">{tr('Minimum')} {inr(info.min_installment)} · {tr('Maximum')} {inr(info.balance)}</p>
                  <button type="submit" disabled={busy} data-testid="pay-balance-submit" className="w-full bg-saffron-600 text-white rounded-full px-6 py-3.5 font-medium hover:bg-saffron-500 transition-colors disabled:opacity-50">{busy ? tr('Processing...') : tr('Pay Now')}</button>
                </form>
              ) : (
                <p className="text-center text-green-700 font-medium" data-testid="pay-balance-fully-paid">{tr('Your fee is fully paid. Thank you!')}</p>
              )}
            </div>
          )}
        </motion.div>
      </section>
    </div>
  );
}
